import { objectWithoutProperties as _objectWithoutProperties, extends as _extends, objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default__default, { useState } from 'react';
import PropTypes__default from 'prop-types';
import styles from './phone-input.scss.js';
import HoneyBem from '../../packages/honey-bem/honey-bem.js';
import CountrySelector from './quarks/country-selector.js';
import { InputInfoChildren } from '../_atoms/input-info/input-info.js';
import { getCallingCodeFromNumber, getCountryByCallingCode, getCountryCodeByCallingCode, normalizePhoneNumber, sanitizePhoneNumber } from './phone-input-utils.js';
import Input from '../input/input.js';
import Label from '../_atoms/label/label.js';
import { Injecss } from '../../packages/injecss/injecss.js';

const _excluded = ["disabled", "extraClasses", "extraClassesAddOn", "label", "info", "infoError", "id", "requiredText", "valid", "callingCodes", "value", "autocomplete", "nativeSelect", "selectAddOn", "selectAddOnSm", "selectRest", "selectLabel"];
const bem = HoneyBem.init('phone-input', styles);
const PhoneInput = _ref => {
  let {
      disabled,
      extraClasses,
      extraClassesAddOn,
      label,
      info,
      infoError,
      id,
      requiredText,
      valid,
      callingCodes,
      value,
      autocomplete,
      nativeSelect,
      selectAddOn,
      selectAddOnSm,
      selectRest,
      selectLabel
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  // if no value provided, use first item in the options code list
  const initialCode = value ? getCallingCodeFromNumber(value, callingCodes) : callingCodes[0].value;
  const initialNumber = value || initialCode;

  // initialize state variables using useState hook and populate default values
  // @todo decide if one state object makes more sense
  const [code, setCode] = useState(initialCode);
  const [number, setNumber] = useState(initialNumber);
  const [country, setCountry] = useState(getCountryByCallingCode(initialCode, callingCodes));
  const [countryCode, setCountryCode] = useState(getCountryCodeByCallingCode(initialCode, callingCodes));

  /**
   * Handles country code selection change, replaces phone with the country code if not possible
   * @param e {object} event object from code select
   */
  const handleCountryChange = e => {
    setCode(e.target.value);
    setCountry(getCountryByCallingCode(e.target.value, callingCodes));
    setCountryCode(getCountryCodeByCallingCode(e.target.value, callingCodes));

    // if phone has a code from the list of options, replace it,
    // if not replace phone with the code
    if (code) {
      // replace the code if possible, normalize the number first (make sure it starts with "+")
      const phoneNumberNormalized = normalizePhoneNumber(number);
      setNumber(phoneNumberNormalized.replace(code, e.target.value));
    } else {
      setNumber(sanitizePhoneNumber(e.target.value));
    }
  };

  /**
   * Updates calling code and country if the number was changed in the input field
   * @param e {object} event object from phone input
   */
  const handlePhoneChange = e => {
    const sanitizedNumber = sanitizePhoneNumber(e.target.value);
    const codeFromNumber = getCallingCodeFromNumber(e.target.value, callingCodes);
    setNumber(sanitizedNumber);
    setCode(codeFromNumber);
    setCountry(getCountryByCallingCode(codeFromNumber, callingCodes));
    setCountryCode(getCountryCodeByCallingCode(codeFromNumber, callingCodes));
  };
  const phoneInputClassNames = bem.b({
    'has-select-addon': !nativeSelect,
    'has-select-addon-sm': selectAddOnSm // @todo this needs a better name =(
  }, extraClasses);
  const phoneInputLabel = label && /*#__PURE__*/React__default__default.createElement(Label, {
    text: label,
    labelFor: id,
    requiredText: requiredText
  });
  const selectAddonComponent = !nativeSelect &&
  /*#__PURE__*/
  // if native select is not enforced display custom container with the current value
  React__default__default.createElement("div", {
    className: bem.e('select-addon', false, extraClassesAddOn)
  }, selectAddOn &&
  // if custom render function passed use it's result within the addon
  selectAddOn(code, countryCode, country), !selectAddOn &&
  // default content for addon is displaying the select text/country
  // comparing to the native select this allows us apply styling such as text overflow
  country);
  return /*#__PURE__*/React__default__default.createElement(Input, _extends({
    type: "tel",
    label: phoneInputLabel,
    extraClasses: phoneInputClassNames,
    id: id,
    disabled: disabled,
    info: info,
    infoError: infoError,
    valid: valid,
    value: number,
    inlineLabelLeft: /*#__PURE__*/React__default__default.createElement(React__default__default.Fragment, null, selectAddonComponent, /*#__PURE__*/React__default__default.createElement(CountrySelector, _extends({
      callingCodes: callingCodes,
      onChange: handleCountryChange,
      disabled: disabled,
      value: code,
      "aria-label": selectLabel
    }, selectRest))),
    required: requiredText ? true : _objectSpread2({}, rest).required
  }, rest, autocomplete && {
    autocomplete: 'tel'
  }, {
    onChange: handlePhoneChange,
    hasInlineLabelAddon: true
  }));
};
PhoneInput.displayName = 'PhoneInput';
PhoneInput.defaultProps = {
  disabled: null,
  extraClasses: '',
  extraClassesAddOn: '',
  info: null,
  infoError: null,
  requiredText: null,
  valid: null,
  value: '',
  nativeSelect: false,
  autocomplete: false,
  selectRest: {}
};
PhoneInput.propTypes = {
  /** Country/codes options for the select box,
   * every element should contain value, country and countryCode properties */
  callingCodes: PropTypes__default.arrayOf(PropTypes__default.shape({
    value: PropTypes__default.oneOfType([PropTypes__default.string, PropTypes__default.number]).isRequired,
    countryCode: PropTypes__default.string.isRequired,
    country: PropTypes__default.string.isRequired // @todo figure out if we need a countryName as well
  })).isRequired,
  /** Phone input's default value, you can pre-fill the calling code or even a number if you want */
  value: PropTypes__default.string,
  /** Triggers visual validation output */
  valid: PropTypes__default.bool,
  /** Disables the input if set */
  disabled: PropTypes__default.bool,
  /** Provides label text, required if aria-label is not passed */
  /* eslint-disable react/require-default-props */
  // we want label to be always checked against
  label(_ref2) {
    let {
      'aria-label': ariaLabel,
      label
    } = _ref2;
    // if no aria label nor label throw validation error
    return !ariaLabel && !label ? new Error('Either label or aria label are required') : null;
  },
  /** Custom classes name for input element */
  extraClasses: PropTypes__default.string,
  /** Custom classes name for select add on if provided */
  extraClassesAddOn: PropTypes__default.string,
  /** Provides extra information for this input field */
  info: InputInfoChildren,
  /** Provides extra information for an error, gets displayed only when `valid` is set to false */
  infoError: InputInfoChildren,
  /** Defines element ID that is used to bind it to the label and error messages */
  id: PropTypes__default.oneOfType([PropTypes__default.string, PropTypes__default.number]).isRequired,
  /** Marks the element as required, adds specified text to label text,
   * e.g. Phone (Mandatory) for required="(Mandatory)" */
  requiredText: PropTypes__default.string,
  /** Forces native select to be displayed as callingCode selector */
  nativeSelect: PropTypes__default.bool,
  /** allows customizing what gets displayed as selected value, only works if `nativeSelect: false`
   * accepts code, countryCode, country params */
  selectAddOn: PropTypes__default.func,
  /** Makes addon container smaller in size, perfect for flags */
  selectAddOnSm: PropTypes__default.bool,
  /** `aria-label` applied to the country select element */
  selectLabel: PropTypes__default.string.isRequired,
  /** Enables `autocomplete="tel"` mode on the input */
  autocomplete: PropTypes__default.bool,
  /** Rest props passed to calling code select, for maximum customization flexibility */
  selectRest: PropTypes__default.object
};
var phoneInput = Injecss(PhoneInput, styles);

export { PhoneInput, phoneInput as default };
